import type { TokenPairSymbol, TokenSelectorItem } from "@cede/types";
import {
  forgeTokenURL,
  getBaseToken,
  getQuoteToken,
  isNativeTokenAddress,
  isWrappedNativeTokenAddress,
} from "@cede/utils";

const NATIVE_PAIR_SYMBOLS: Record<string, string> = {
  "1": "ETH",
  "10": "ETH",
  "56": "BNB",
  "137": "MATIC",
  "250": "FTM",
  "42161": "ETH",
  "43114": "AVAX",
  "8453": "ETH",
  "4663": "ETH",
  "solana": "SOL",
};

/**
 * pairSymbols are stored as symbols like "SOL/MCV", not addresses.
 * For native gas tokens we therefore need one small chain-specific mapping.
 */
const getNativePairSymbol = (chainId: string): string | undefined => {
  return NATIVE_PAIR_SYMBOLS[chainId];
};

/**
 * Campaign pair rules are stricter than generic token display rules.
 * On Solana, "SOL" in a campaign pair means native SOL only.
 */
const isNativePairToken = (token: Pick<TokenSelectorItem, "address">, chainId: string): boolean => {
  if (chainId === "solana") {
    return isNativeTokenAddress(token.address, chainId);
  }

  return isNativeTokenAddress(token.address, chainId) || isWrappedNativeTokenAddress(token.address, chainId);
};

export const tokenMatchesWhitelistedPairSymbol = (
  token: Pick<TokenSelectorItem, "address" | "symbol">,
  whitelistedSymbol: string,
  chainId: string,
): boolean => {
  // Most pair entries can match by symbol directly. Native gas tokens are the
  // only exception because pairSymbols are symbol-based while the selector holds addresses.
  const normalizedWhitelistedSymbol = whitelistedSymbol.toUpperCase();
  const nativePairSymbol = getNativePairSymbol(chainId);

  if (nativePairSymbol && normalizedWhitelistedSymbol === nativePairSymbol) {
    return isNativePairToken(token, chainId);
  }

  return token.symbol.toUpperCase() === normalizedWhitelistedSymbol;
};

export const isOnchainPairWhitelisted = (
  tokenFrom: Pick<TokenSelectorItem, "address" | "symbol">,
  tokenTo: Pick<TokenSelectorItem, "address" | "symbol">,
  pairSymbols: TokenPairSymbol[],
  chainId: string,
): boolean => {
  // Match in both directions because pairSymbols are unordered spot pairs.
  return pairSymbols.some((pair) => {
    const base = getBaseToken(pair).toUpperCase();
    const quote = getQuoteToken(pair).toUpperCase();
    if (base === "UNKNOWN" || quote === "UNKNOWN") return false;

    return (
      (tokenMatchesWhitelistedPairSymbol(tokenFrom, base, chainId) &&
        tokenMatchesWhitelistedPairSymbol(tokenTo, quote, chainId)) ||
      (tokenMatchesWhitelistedPairSymbol(tokenFrom, quote, chainId) &&
        tokenMatchesWhitelistedPairSymbol(tokenTo, base, chainId))
    );
  });
};

/**
 * Extracts all unique tokens from whitelisted pairs.
 * e.g., ["ETH/USDC", "BTC/USDT"] returns ["ETH", "USDC", "BTC", "USDT"]
 */
export function getAllTokensFromPairs(pairSymbols: TokenPairSymbol[]): string[] {
  const tokens = new Set<string>();
  for (const pair of pairSymbols) {
    const base = getBaseToken(pair);
    const quote = getQuoteToken(pair);
    if (base !== "unknown") tokens.add(base.toUpperCase());
    if (quote !== "unknown") tokens.add(quote.toUpperCase());
  }
  return Array.from(tokens);
}

export const filterOnchainTokensByPairSymbols = (
  tokens: TokenSelectorItem[],
  pairSymbols: TokenPairSymbol[],
  chainId: string,
): TokenSelectorItem[] => {
  if (!pairSymbols?.length) return tokens;

  // Source selector: show any token that participates in at least one allowed pair.
  const whitelistedSymbols = getAllTokensFromPairs(pairSymbols);
  return tokens.filter((token) =>
    whitelistedSymbols.some((symbol) => tokenMatchesWhitelistedPairSymbol(token, symbol, chainId)),
  );
};

/**
 * Ensures all tokens from whitelisted pairs are included in the assets list,
 * even if user has zero balance. Tokens not owned by user get a placeholder
 * with needsTopUp: true flag.
 */
export function ensureWhitelistedPairTokensIncluded<T extends { value: string; freeBalance?: number }>(
  existingAssets: T[],
  pairSymbols: TokenPairSymbol[],
): Array<T & { needsTopUp?: boolean }> {
  if (!pairSymbols?.length) return existingAssets;

  const allWhitelistedTokens = getAllTokensFromPairs(pairSymbols);
  const existingTokens = new Set(existingAssets.map((a) => a.value.toUpperCase()));

  // Start with existing assets, filtered to whitelisted tokens
  // Always show Top Up option for whitelisted tokens so users can easily acquire more
  const result: Array<T & { needsTopUp?: boolean }> = existingAssets
    .filter((asset) => allWhitelistedTokens.includes(asset.value.toUpperCase()))
    .map((asset) => ({
      ...asset,
      needsTopUp: true, // Always show Top Up for whitelisted pair tokens
    }));

  // Add placeholder entries for missing tokens
  for (const token of allWhitelistedTokens) {
    if (!existingTokens.has(token)) {
      result.push({
        label: token,
        value: token,
        img: forgeTokenURL(token),
        balance: "0",
        freeBalance: 0,
        amount: "0",
        needsTopUp: true,
      } as unknown as T & { needsTopUp: boolean });
    }
  }

  return result;
}

/**
 * Checks if a trade pair (tokenFrom -> tokenTo) matches any whitelisted pair.
 * Matches in either direction (ETH/USDC matches both ETH->USDC and USDC->ETH).
 */
export function isPairWhitelisted(tokenFrom: string, tokenTo: string, pairSymbols: TokenPairSymbol[]): boolean {
  const normalizedFrom = tokenFrom.toUpperCase();
  const normalizedTo = tokenTo.toUpperCase();

  return pairSymbols.some((pair) => {
    const base = getBaseToken(pair).toUpperCase();
    const quote = getQuoteToken(pair).toUpperCase();
    if (base === "UNKNOWN" || quote === "UNKNOWN") return false;

    // Match in either direction
    return (base === normalizedFrom && quote === normalizedTo) || (base === normalizedTo && quote === normalizedFrom);
  });
}

/**
 * Filters destination tokens based on pairSymbols whitelist.
 * When pairSymbols is set, Trade Square is disabled and ONLY direct trades
 * on whitelisted pairs are allowed.
 *
 * e.g., with whitelist ["ETH/USDC"]:
 * - From ETH: only USDC is shown
 * - From USDC: only ETH is shown
 * - From USDT: nothing is shown (USDT is not in any whitelisted pair)
 *
 * @param assetsTo - Available destination tokens
 * @param pairSymbols - Whitelisted trading pairs
 * @param tokenFrom - Currently selected source token
 */
export function filterByWhitelistedPairs<T extends { value: string }>(
  assetsTo: T[],
  pairSymbols: TokenPairSymbol[],
  tokenFrom: string,
): T[] {
  if (!pairSymbols?.length) return assetsTo;

  // Only allow destinations that form a whitelisted pair with tokenFrom
  return assetsTo.filter((asset) => {
    return isPairWhitelisted(tokenFrom, asset.value, pairSymbols);
  });
}
